import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getBattery, compatibleDevices } from '../services/batteries';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';

export function BatteryDetailPage() {
  const { id } = useParams<{ id: string }>();

  const batteryQ = useQuery({
    queryKey: ['battery', id],
    queryFn: () => getBattery(id!),
    enabled: !!id,
  });

  const devicesQ = useQuery({
    queryKey: ['battery', id, 'devices'],
    queryFn: () => compatibleDevices(id!),
    enabled: !!id,
  });

  if (batteryQ.isLoading) return <Spinner />;
  if (batteryQ.isError || !batteryQ.data) {
    return <Card className="text-accent-red">Battery not found.</Card>;
  }

  const b = batteryQ.data.battery;

  return (
    <div className="flex flex-col gap-8">
      <header>
        <div className="text-xs uppercase text-neutral-400">{b.chemistry}</div>
        <h1>{b.modelCode}</h1>
      </header>

      <section>
        <Card>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <dt className="text-neutral-400">Capacity</dt>
              <dd className="text-lg font-semibold">{b.capacityMah} mAh</dd>
            </div>
            <div>
              <dt className="text-neutral-400">Voltage</dt>
              <dd className="text-lg font-semibold">{b.voltage} V</dd>
            </div>
            {b.weightG !== undefined && (
              <div>
                <dt className="text-neutral-400">Weight</dt>
                <dd className="text-lg font-semibold">{b.weightG} g</dd>
              </div>
            )}
            {b.dimensionsMm && (
              <div>
                <dt className="text-neutral-400">Dimensions</dt>
                <dd className="text-lg font-semibold">
                  {b.dimensionsMm.w}×{b.dimensionsMm.h}×{b.dimensionsMm.d} mm
                </dd>
              </div>
            )}
          </dl>
        </Card>
      </section>

      <section>
        <h2 className="mb-4">Compatible devices</h2>
        {devicesQ.isLoading && <Spinner />}
        {devicesQ.isError && (
          <Card className="text-accent-red">Could not load compatible devices.</Card>
        )}
        {devicesQ.isSuccess && devicesQ.data.length === 0 && (
          <Card className="text-neutral-400">No devices linked to this battery yet.</Card>
        )}
        {devicesQ.isSuccess && devicesQ.data.length > 0 && (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {devicesQ.data.map((d) => (
              <li key={d._id}>
                <Link to={`/devices/${d._id}`} className="card block hover:border-lime-500">
                  <div className="text-xs uppercase text-neutral-400">{d.brand}</div>
                  <div className="text-lg font-semibold">{d.name}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
